import { useState, useEffect } from 'react'
import { useAIStore } from '../stores/aiStore'
import { useResearchStore } from '../stores/researchStore'
import { Button, Input, Select, Card } from '../components/ui'
import { toast } from '../components/ui/Toast'
import type { AIProvider } from '../types'
import { AI_PROVIDERS } from '../types'

const PROVIDER_OPTIONS = [
  { value: '', label: 'Select provider...' },
  ...Object.entries(AI_PROVIDERS).map(([value, { name }]) => ({
    value,
    label: name,
  })),
]

export default function Settings() {
  const {
    isInitialised,
    isSupported,
    isLoading,
    isSaving,
    isValidating,
    error,
    provider: savedProvider,
    keyFingerprint,
    wasKeyReset,
    saveApiKey,
    validateKey,
    clearApiKey,
    clearError,
  } = useAIStore()

  const [provider, setProvider] = useState<AIProvider | ''>('')
  const [apiKey, setApiKey] = useState('')
  const [validationState, setValidationState] = useState<{
    validated: boolean
    valid: boolean
    warning?: string
    error?: string
  } | null>(null)
  const [showClearConfirm, setShowClearConfirm] = useState(false)

  // Sync form state with saved settings
  useEffect(() => {
    if (savedProvider) {
      setProvider(savedProvider)
    }
  }, [savedProvider])

  // Clear validation when inputs change
  useEffect(() => {
    setValidationState(null)
  }, [provider, apiKey])

  // Show error toast
  useEffect(() => {
    if (error) {
      toast(error, 'error')
      clearError()
    }
  }, [error, clearError])

  // Show key reset warning
  useEffect(() => {
    if (wasKeyReset) {
      toast('Your API key could not be decrypted and has been cleared. Please re-enter it.', 'error')
      clearError()
    }
  }, [wasKeyReset, clearError])

  const handleValidate = async () => {
    if (!provider || !apiKey) return

    const result = await validateKey(provider as AIProvider, apiKey)
    setValidationState({
      validated: true,
      valid: result.valid,
      warning: result.warning,
      error: result.error,
    })

    if (result.valid) {
      toast(result.warning || 'API key is valid', result.warning ? 'info' : 'success')
    } else {
      toast(result.error || 'Invalid API key', 'error')
    }
  }

  const handleSave = async () => {
    if (!provider || !apiKey) return

    // Require validation first
    if (!validationState?.validated) {
      toast('Please validate the API key first', 'info')
      return
    }

    if (!validationState.valid && !validationState.warning) {
      toast('Cannot save an invalid API key', 'error')
      return
    }

    const result = await saveApiKey(provider as AIProvider, apiKey)
    
    if (result.success) {
      toast('AI provider settings saved', 'success')
      setApiKey('') // Clear the input after saving
      setValidationState(null)
    }
  }

  const handleClear = async () => {
    await clearApiKey()
    setProvider('')
    setApiKey('')
    setValidationState(null)
    setShowClearConfirm(false)
    toast('AI provider settings cleared', 'success')
  }

  if (!isInitialised || isLoading) {
    return (
      <div className="max-w-2xl">
        <h1 className="text-2xl font-semibold text-text-primary mb-6">Settings</h1>
        <Card>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-6 h-6 border-2 border-action-primary border-t-transparent rounded-full" />
          </div>
        </Card>
      </div>
    )
  }

  if (!isSupported) {
    return (
      <div className="max-w-2xl">
        <h1 className="text-2xl font-semibold text-text-primary mb-6">Settings</h1>
        <Card>
          <div className="text-center py-8">
            <p className="text-text-secondary">
              Your browser doesn't support the encryption required for secure API key storage.
            </p>
            <p className="text-sm text-text-secondary mt-2">
              Please use a modern browser with Web Crypto API support.
            </p>
          </div>
        </Card>
      </div>
    )
  }

  const isConfigured = Boolean(savedProvider && keyFingerprint)
  const hasChanges = apiKey.length > 0 || (provider && provider !== savedProvider)
  const canValidate = provider && apiKey && !isValidating
  const canSave = validationState?.validated && (validationState.valid || validationState.warning) && !isSaving

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-semibold text-text-primary mb-6">Settings</h1>

      <Card>
        <section>
          <h2 className="text-lg font-medium text-text-primary mb-4">AI Provider</h2>
          
          <p className="text-sm text-text-secondary mb-6">
            Configure an AI provider to enable intelligent features. Your API key is encrypted 
            before storage using keys derived from your Google account. We cannot access it.
          </p>

          {isConfigured && !hasChanges && (
            <div className="mb-6 p-4 bg-[#EDF5EF] border border-success rounded-[6px]">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-text-primary">
                    {AI_PROVIDERS[savedProvider!].name}
                  </span>
                  <KeyFingerprint fingerprint={keyFingerprint!} />
                </div>
                <span className="text-sm text-success">Configured</span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <Select
              label="Provider"
              options={PROVIDER_OPTIONS}
              value={provider}
              onChange={(e) => setProvider(e.target.value as AIProvider | '')}
              disabled={isSaving}
            />

            <div>
              <Input
                label="API Key"
                type="password"
                placeholder={provider ? AI_PROVIDERS[provider as AIProvider]?.placeholder : 'Select a provider first'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={!provider || isSaving}
              />
              {isConfigured && !apiKey && (
                <p className="text-xs text-text-secondary mt-1">
                  Leave blank to keep existing key, or enter a new key to replace it.
                </p>
              )}
            </div>

            {validationState && (
              <ValidationResult
                valid={validationState.valid}
                warning={validationState.warning}
                error={validationState.error}
              />
            )}

            <div className="flex items-center gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={handleValidate}
                disabled={!canValidate}
              >
                {isValidating ? 'Validating...' : 'Validate'}
              </Button>
              
              <Button
                onClick={handleSave}
                disabled={!canSave}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>

              {isConfigured && (
                <Button
                  variant="ghost"
                  onClick={() => setShowClearConfirm(true)}
                  disabled={isSaving}
                  className="ml-auto"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </section>
      </Card>

      <ResearchSettings />

      {showClearConfirm && (
        <ClearConfirmDialog
          onConfirm={handleClear}
          onCancel={() => setShowClearConfirm(false)}
          isSaving={isSaving}
        />
      )}
    </div>
  )
}

function ResearchSettings() {
  const { settings, settingsLoaded, loadSettings, saveSettings } = useResearchStore()
  const [autoResearch, setAutoResearch] = useState(settings.autoResearchNewCompanies)
  const [includeCompaniesHouse, setIncludeCompaniesHouse] = useState(settings.includeCompaniesHouse)
  const [businessContext, setBusinessContext] = useState(settings.businessContext)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!settingsLoaded) {
      loadSettings()
    }
  }, [settingsLoaded, loadSettings])

  useEffect(() => {
    setAutoResearch(settings.autoResearchNewCompanies)
    setIncludeCompaniesHouse(settings.includeCompaniesHouse)
    setBusinessContext(settings.businessContext)
  }, [settings])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await saveSettings({
        autoResearchNewCompanies: autoResearch,
        includeCompaniesHouse: includeCompaniesHouse,
        businessContext: businessContext.trim(),
      })
      toast('Research settings saved', 'success')
    } catch {
      toast('Failed to save settings', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const hasChanges =
    autoResearch !== settings.autoResearchNewCompanies ||
    includeCompaniesHouse !== settings.includeCompaniesHouse ||
    businessContext.trim() !== settings.businessContext

  return (
    <Card className="mt-6">
      <section>
        <h2 className="text-lg font-medium text-text-primary mb-4">Prospect Research</h2>

        <p className="text-sm text-text-secondary mb-6">
          Configure how prospect research runs when you add or view companies.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Your business
            </label>
            <textarea
              value={businessContext}
              onChange={(e) => setBusinessContext(e.target.value)}
              placeholder="e.g. Web design agency specialising in Shopify stores for fashion brands"
              rows={3}
              className="w-full px-3 py-2.5 text-sm bg-bg-surface border border-border rounded-[6px] transition-[border-color,box-shadow] duration-150 ease-out placeholder:text-text-secondary focus:outline-none focus:border-action-primary focus:ring-[3px] focus:ring-action-focus resize-none"
            />
            <p className="text-xs text-text-secondary mt-1">
              Describe what you do. This helps AI assess how well prospects fit your services.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoResearch}
              onChange={(e) => setAutoResearch(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border text-action-primary focus:ring-action-focus"
            />
            <div>
              <span className="text-sm text-text-primary">Auto-research new companies</span>
              <p className="text-xs text-text-secondary mt-0.5">
                Run research automatically when adding a company
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeCompaniesHouse}
              onChange={(e) => setIncludeCompaniesHouse(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border text-action-primary focus:ring-action-focus"
            />
            <div>
              <span className="text-sm text-text-primary">Include Companies House lookup</span>
              <p className="text-xs text-text-secondary mt-0.5">
                Search UK company registry for firmographic data
              </p>
            </div>
          </label>

          <div className="pt-2">
            <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </section>
    </Card>
  )
}

function KeyFingerprint({ fingerprint }: { fingerprint: string }) {
  const colour = `#${fingerprint}`
  
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono"
      style={{
        backgroundColor: colour,
        color: isLightColour(colour) ? '#1A1A1A' : '#FFFFFF',
      }}
      title={`Key fingerprint: ${fingerprint}`}
    >
      #{fingerprint}
    </span>
  )
}

function isLightColour(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5
}

function ValidationResult({
  valid,
  warning,
  error,
}: {
  valid: boolean
  warning?: string
  error?: string
}) {
  if (valid && !warning) {
    return (
      <div className="flex items-center gap-2 text-sm text-success">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.78 5.97l-4.5 5.5a.75.75 0 0 1-1.14.02l-2-2.25a.75.75 0 1 1 1.12-1l1.42 1.6 3.96-4.84a.75.75 0 1 1 1.16.95l-.02.02z" />
        </svg>
        <span>API key is valid</span>
      </div>
    )
  }

  if (valid && warning) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#B58105]">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-3a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 8 5zm0 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
        </svg>
        <span>{warning}</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 text-sm text-error">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.28 5.22a.75.75 0 0 1 0 1.06L9.06 8.5l2.22 2.22a.75.75 0 1 1-1.06 1.06L8 9.56l-2.22 2.22a.75.75 0 0 1-1.06-1.06L6.94 8.5 4.72 6.28a.75.75 0 0 1 1.06-1.06L8 7.44l2.22-2.22a.75.75 0 0 1 1.06 0z" />
      </svg>
      <span>{error || 'Validation failed'}</span>
    </div>
  )
}

function ClearConfirmDialog({
  onConfirm,
  onCancel,
  isSaving,
}: {
  onConfirm: () => void
  onCancel: () => void
  isSaving: boolean
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/40" 
        onClick={onCancel}
        aria-hidden="true"
      />
      <div className="relative bg-bg-surface rounded-lg shadow-modal p-6 max-w-sm mx-4">
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          Clear API Key?
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          This will remove your saved API key. AI features will be disabled until you configure a new key.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isSaving}>
            {isSaving ? 'Clearing...' : 'Clear'}
          </Button>
        </div>
      </div>
    </div>
  )
}
